import {useFormikContext} from 'formik';
import React, {useContext, useEffect, useMemo, useState} from 'react';
import Loading from 'shared/components/loading';
import BaseStep from 'shared/registration/components/steps/base-step';
import FooterTextWithEmoji from 'shared/student/registration/components/footer-text-with-emoji';
import CollegeCard from 'shared/student/registration/components/steps/college-discovery-step/college-card';
import {ImpostorModeContext} from 'src/components/ImpostorModeContext';
import {OnboardingContext} from 'src/components/Onboarding/context';
import {useMediaQuery} from 'src/hooks/useMediaQuery';
import UserService from 'src/services/UserService';
import {AchievementOption, InstitutionSuggestion} from 'src/types/education';
import {Emojis} from 'src/types/enums';
import {FollowCollegesForm} from 'src/types/onboarding';
import getText from './getText';

export default function FollowColleges(): React.ReactElement {
  const onboardingContext = useContext(OnboardingContext);
  const form = useFormikContext<FollowCollegesForm>();
  const impostorModeContext = useContext(ImpostorModeContext);
  const {firstName, highSchoolInfo} = form.values;
  const mq = useMediaQuery();

  const {title, subtitle, footerText, lateSeniorSignup, disclaimer} = useMemo(
    () =>
      getText(
        mq.MS_AND_DOWN,
        firstName,
        impostorModeContext.isImpostor,
        highSchoolInfo.graduationYear,
        form.values.educationPhase
      ),
    [impostorModeContext.isImpostor, mq.MS_AND_DOWN, firstName]
  );
  const [suggestions, setSuggestions] = useState<InstitutionSuggestion[] | null>(null);
  const [achievementOptions, setAchievementOptions] = useState<AchievementOption[]>();

  useEffect(() => {
    let text = form.values.followedCollegeIds.length
      ? `Follow ${form.values.followedCollegeIds.length}`
      : 'Skip';
    if (suggestions && mq.SM_AND_UP) {
      text = `${text} & Continue`;
    }

    onboardingContext.updatePrimaryButton({
      text,
      controlled: true,
      disabled: !suggestions,
    });
  }, [mq.SM_AND_UP, form.values.followedCollegeIds]);

  function onFollowCollege(collegeId: string): void {
    const ids = form.values.followedCollegeIds.concat(collegeId);
    form.setFieldValue('followedCollegeIds', ids);
  }

  function onUnFollowCollege(collegeId: string): void {
    const ids = form.values.followedCollegeIds.filter((id) => id !== collegeId);
    form.setFieldValue('followedCollegeIds', ids);
  }

  function bulkFollow(collegesToFollow: InstitutionSuggestion[]): void {
    if (!collegesToFollow) return;
    const ids = collegesToFollow.map((college) => college.id);
    form.setFieldValue('followedCollegeIds', ids);
  }

  useEffect(() => {
    UserService.getPotentialPortfolioItems().then((items) => {
      setAchievementOptions(items);
    });
  }, []);

  useEffect(() => {
    if (!achievementOptions) return;
    UserService.getSuggestedColleges(
      achievementOptions.map((option) => option.scholarshipType),
      8
    ).then((suggestedColleges) => {
      setSuggestions(suggestedColleges);
      setTimeout(() => bulkFollow(suggestedColleges), 500);
    });
  }, [achievementOptions]);

  const followedIdSet = new Set(form.values.followedCollegeIds);

  return (
    <BaseStep
      stepHeader={title}
      subHeader={subtitle}
      lateSeniorSignup={lateSeniorSignup}
      disclaimer={disclaimer}
      className="achievement-selection-step"
    >
      {Array.isArray(suggestions) ? (
        suggestions.map((college) => (
          <CollegeCard
            id={college.id}
            key={college.id}
            city={college.city}
            name={college.name}
            slug={college.slug}
            state={college.state}
            microScholarships={college.microScholarships}
            following={followedIdSet.has(college.id)}
            followCollege={onFollowCollege}
            unfollowCollege={onUnFollowCollege}
            potentialPortfolioItems={achievementOptions || []}
          />
        ))
      ) : (
        <Loading />
      )}
      <FooterTextWithEmoji emoji={Emojis.INSTITUTION} text={footerText} />
    </BaseStep>
  );
}
